import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search } from "lucide-react";

const ROLES = ["worker", "approver", "admin", "tenant_admin"];

export default function DirectoryPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = {};
      if (q) params.q = q;
      if (role !== "all") params.role = role;
      if (status !== "all") params.status = status;
      const { data } = await api.get("/tenant/users", { params });
      setItems(data.items);
      setLoading(false);
    })();
  }, [q, role, status]);

  return (
    <div className="space-y-6" data-testid="directory-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">People</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Directory</h1>
        <p className="mt-2 text-muted-foreground">Search and filter people on this workspace.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_180px_180px]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search by name or email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="directory-search"
          />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="directory-role-filter"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="directory-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="invited">Invited</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Roles</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>MFA</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="directory-tbody">
            {loading && (
              <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">No people match those filters.</TableCell></TableRow>
            )}
            {items.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.full_name || <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {u.roles.map((r) => (
                      <Badge key={r} variant="secondary" className="rounded-full">{r}</Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <StatusPill status={u.status} />
                </TableCell>
                <TableCell>
                  {u.mfa_enabled ? (
                    <Badge className="bg-success/15 text-success hover:bg-success/15">On</Badge>
                  ) : (
                    <span className="text-muted-foreground">Off</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    active: "bg-success/15 text-success",
    invited: "bg-warning/15 text-warning-foreground",
    disabled: "bg-destructive/15 text-destructive",
  };
  return <Badge className={`${map[status]} rounded-full capitalize hover:${map[status]}`}>{status}</Badge>;
}
